import { store } from "@/store";
import { vuexName, MODULES } from "@/store/utils";
import Ably from "ably";
import { ReconnectCoordinator, ReconnectContext, OfflineState } from "@/ws/reconnect-coordinator";
import type {
  ClientOptions as AblyClientOptions,
  ConnectionStateChange as AblyConnectionStateChange,
  Message as AblyMessage,
  Realtime as AblyRealtime,
  RealtimeChannel as AblyRealtimeChannel,
  messageCallback as AblyMessageCallback,
} from "ably";
import { RealtimeHandlerMap, createRealtimeHandlerKey } from "@/ws/handler";
import { RealtimeMessage } from "@/ws/models";
import type { SessionRealtimeChannelsModel } from "@/models";
import type { SessionRealtimeChannelKey } from "@/ws/channels";

export interface GLSocketReconnectContext {
  // Kept for backward compatibility, but no longer includes transport field
}

export interface RealtimeLifecycleCallbacks {
  onReconnecting: () => void;
  onReconnected: (connectionId?: string, context?: ReconnectContext) => Promise<void> | void;
  onDisconnected: () => void;
}

export interface RealtimeTransportOptions {
  channelName: string | string[] | (() => string | string[]);
  clientOptions: AblyClientOptions;
  channelsConfig?: SessionRealtimeChannelsModel;
  messageEncoder?: (payload: any) => any;
  messageDecoder?: (payload: any) => any;
}

export interface SendToChannelConfig<T = any> {
  event: string;
  action: string;
  data: T;
  senderId?: string;
  errorContext?: string;
}

export interface RealtimeClientOptions {
  url: string;
  reConnectedCallback: (newConnectionId: string | undefined, context: GLSocketReconnectContext) => Promise<any>;
  studentId?: string;
  realtime: RealtimeTransportOptions;
  /** ReconnectCoordinator instance for this client (per-manager) */
  coordinator?: ReconnectCoordinator;
}

export class RealtimeClient {
  private realtime?: AblyRealtime;
  private channelMap = new Map<string, AblyRealtimeChannel>();
  private isOnline = false;
  private desiredChannelNames: string[];
  private realtimeHandlerMap?: RealtimeHandlerMap;
  private realtimeListener?: AblyMessageCallback<AblyMessage>;
  private manualChannelOverride = false;
  private channels: SessionRealtimeChannelsModel | undefined;
  private _isConnected = false;
  private hasConnectedBefore = false;

  // State tracking for reconnection decisions (Ably semantics)
  /** Timestamp when connection first went offline (DISCONNECTED or SUSPENDED) */
  private offlineSince: number | null = null;
  /** Last offline state before reconnection */
  private lastOfflineState: OfflineState = null;
  /** Previous connection state for transition tracking */
  private previousConnectionState: string = "initialized";

  // Idempotent connect: track in-flight connect promise
  private connectPromise: Promise<void> | null = null;

  // Channel operation mutex: serialize all subscribe/attach operations
  private channelOpMutex: Promise<void> = Promise.resolve();

  // ReconnectCoordinator instance (injected per-manager)
  private coordinator: ReconnectCoordinator | null = null;

  private handleTransportReconnecting = () => {
    this.markAttemptedReconnecting(true);
  };

  private handleTransportReconnected = async (_connectionId?: string, context?: ReconnectContext) => {
    this.markAttemptedReconnecting(false);
    // Note: syncConnectionState() is called AFTER resync pipeline completes
    // to avoid triggering Vue watchers prematurely
    if (context && this.coordinator) {
      // Let coordinator decide whether to run resync
      await this.coordinator.handleReconnection(context);
    }
    // Now sync connection state after pipeline completes
    this.syncConnectionState();
  };

  private handleTransportDisconnected = () => {
    this.syncConnectionState();
    if (!this._isConnected) {
      this.markAttemptedReconnecting(false);
    }
  };

  private readonly lifecycleCallbacks: RealtimeLifecycleCallbacks = {
    onReconnecting: this.handleTransportReconnecting,
    onReconnected: this.handleTransportReconnected,
    onDisconnected: this.handleTransportDisconnected,
  };

  constructor(private readonly _options: RealtimeClientOptions) {
    this.desiredChannelNames = [];
    if (_options.realtime.channelsConfig) {
      this.channels = _options.realtime.channelsConfig;
    }
    if (_options.coordinator) {
      this.coordinator = _options.coordinator;
    }
  }

  /**
   * Set or update the ReconnectCoordinator instance
   */
  setCoordinator(coordinator: ReconnectCoordinator): void {
    this.coordinator = coordinator;
  }

  /**
   * Execute an async operation within the channel operation mutex
   * This ensures all subscribe/attach/detach operations are serialized
   */
  private async withChannelOpMutex<T>(operation: () => Promise<T>): Promise<T> {
    // Chain operation onto the mutex
    const previousMutex = this.channelOpMutex;
    let resolve: () => void;
    this.channelOpMutex = new Promise<void>((r) => {
      resolve = r;
    });

    try {
      // Wait for any previous operation to complete
      await previousMutex;
      // Execute the operation
      return await operation();
    } finally {
      // Release the mutex
      resolve!();
    }
  }

  get options(): RealtimeClientOptions {
    return this._options;
  }

  get isConnected(): boolean {
    return this._isConnected;
  }

  /** Returns true only when Ably connection is in 'connected' state (not 'connecting', 'disconnected', etc.) */
  get isAblyConnected(): boolean {
    return this.realtime?.connection.state === "connected";
  }

  async init(): Promise<void> {
    if (this.realtime) return;
    this.realtime = new Ably.Realtime(this.options.realtime.clientOptions);
    this.realtime.connection.on((stateChange: any) => this.handleConnectionStateChange(stateChange));
  }

  async connect(_ = false): Promise<void> {
    // Idempotent: if already connecting, return existing promise
    if (this.connectPromise) {
      return this.connectPromise;
    }

    await this.init();
    if (!this.realtime) return;
    const connection = this.realtime.connection;

    // Already connected
    if (connection.state === "connected") {
      this.isOnline = true;
      this.syncConnectionState();
      return;
    }

    // Create and track the connect promise
    this.connectPromise = (async () => {
      try {
        this.realtime!.connect();
        await new Promise<void>((resolve, reject) => {
          const cleanup = () => {
            connection.off("connected", handleConnected);
            connection.off("failed", handleFailure);
            connection.off("closed", handleFailure);
          };
          const handleConnected = () => {
            cleanup();
            this.isOnline = true;
            resolve();
          };
          const handleFailure = (state: AblyConnectionStateChange) => {
            cleanup();
            this.isOnline = false;
            reject(state.reason ?? new Error("Unable to connect to realtime provider"));
          };
          connection.on("connected", handleConnected);
          connection.on("failed", handleFailure);
          connection.on("closed", handleFailure);
        });
        this.syncConnectionState();
      } finally {
        this.connectPromise = null;
      }
    })();

    return this.connectPromise;
  }

  async disconnect(): Promise<void> {
    if (!this.realtime) return;
    this.unsubscribeRealtimeHandlersFromAllChannels();
    await Promise.all(
      Array.from(this.channelMap.values()).map((channel) =>
        channel.detach().catch((_error: any) => { /* Logger.warn(`Failed to detach realtime channel ${channel.name}`, error) */ }),
      ),
    );
    this.channelMap.clear();
    await this.realtime.close();
    this.isOnline = false;
    this.hasConnectedBefore = false;
    // Clear offline tracking state
    this.offlineSince = null;
    this.lastOfflineState = null;
    this.previousConnectionState = "initialized";
    this.connectPromise = null;
    this.syncConnectionState();
  }

  /**
   * Update realtime channel subscriptions
   * Serialized behind channelOpMutex to prevent race conditions
   *
   * Channels are always attached immediately (to allow publishing).
   * Subscription (receiving messages) only happens after handlers are registered.
   */
  async setRealtimeChannels(channelNames: string[]) {
    return this.withChannelOpMutex(async () => {
      this.manualChannelOverride = true;
      const newChannelNames = this.normalizeChannelNames(channelNames);
      // Check if channels are actually different from desired (not channelMap, since we may not have synced yet)
      const currentDesired = [...this.desiredChannelNames].sort();
      const newChannelsSorted = [...newChannelNames].sort();
      const channelsChanged = JSON.stringify(currentDesired) !== JSON.stringify(newChannelsSorted);
      if (!channelsChanged && this.desiredChannelNames.length > 0) {
        return;
      }

      this.desiredChannelNames = newChannelNames;

      // Always attach channels (required for publishing)
      await this.ensureConnected();
      await this.syncChannelsInternal();

      // Only subscribe if handlers are ready
      // If not, subscription will happen when registerRealtimeEventHandler() is called
      if (this.realtimeListener) {
        this.subscribeRealtimeHandlerToAllChannels();
      } else {
        //
      }
    });
  }

  /**
   * Register realtime event handlers
   * Serialized behind channelOpMutex to prevent race conditions
   *
   * This method will also sync and attach any channels that were deferred by setRealtimeChannels()
   * when handlers weren't ready yet.
   */
  async registerRealtimeEventHandler(handlerMap: RealtimeHandlerMap) {
    return this.withChannelOpMutex(async () => {
      await this.ensureConnected();
      this.realtimeHandlerMap = handlerMap;
      this.unsubscribeRealtimeHandlersFromAllChannels();

      // Set up the listener FIRST before syncing channels
      // This ensures we don't miss any messages after channels are attached
      this.realtimeListener = (message: any) => {
        this.handleRealtimeMessage(message).catch((_error) => {
          // Logger.error("Failed to process realtime message", error);
        });
      };

      // Always sync channels here because setRealtimeChannels() may have deferred the attach
      // when handlers weren't ready yet
      if (this.desiredChannelNames.length > 0) {
        await this.syncChannelsInternal();
      }

      this.subscribeRealtimeHandlerToAllChannels();

      // Ensure all channels are attached and ready to receive messages
      await this.ensureAllChannelsAttachedInternal();
    });
  }

  getRealtimeTransport(): RealtimeClient {
    return this;
  }

  getRealtimeClient(): AblyRealtime | undefined {
    return this.realtime;
  }

  /**
   * Get list of currently joined channel names
   */
  getJoinedChannels(): string[] {
    return Array.from(this.channelMap.keys());
  }

  /**
   * Get the channel name for a given channel key
   */
  getChannelNameByKey(channelKey: SessionRealtimeChannelKey): string | undefined {
    return this.channels?.[channelKey];
  }

  /**
   * Set the session's realtime channels configuration
   * This is required for using the sendToMain, sendToTeacherOneOne, etc. methods
   */
  setChannelsConfig(channels: SessionRealtimeChannelsModel): void {
    this.channels = channels;
  }

  async sendToMain<T = any>(config: SendToChannelConfig<T>): Promise<boolean> {
    return this.sendToSpecificChannel(config, this.channels?.main);
  }

  async sendToTeacherOneOne<T = any>(config: SendToChannelConfig<T>): Promise<boolean> {
    return this.sendToSpecificChannel(config, this.channels?.oneOne);
  }

  async sendToHelperOneOne<T = any>(config: SendToChannelConfig<T>): Promise<boolean> {
    return this.sendToSpecificChannel(config, this.channels?.helperOneOne);
  }

  async sendToTeacherHelper<T = any>(config: SendToChannelConfig<T>): Promise<boolean> {
    return this.sendToSpecificChannel(config, this.channels?.teacherHelper);
  }

  async sendToAll<T = any>(config: SendToChannelConfig<T>): Promise<boolean> {
    const results = await Promise.allSettled([this.sendToMain(config), this.sendToTeacherOneOne(config), this.sendToHelperOneOne(config)]);

    return results.every((result) => result.status === "fulfilled" && result.value === true);
  }

  private syncConnectionState() {
    this._isConnected = this.isOnline;

    // Update Vuex store to trigger reactivity for both teacher and student rooms
    store.commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_IS_CONNECTED), this._isConnected);
    store.commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_IS_CONNECTED), this._isConnected);
  }

  markAttemptedReconnecting = (isReconnecting: boolean) => {
    store.commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_IS_SOCKET_ATTEMPTED_RECONNECTING), isReconnecting);
  };

  private handleConnectionStateChange(stateChange: AblyConnectionStateChange) {
    const previousState = this.previousConnectionState;
    this.previousConnectionState = stateChange.current;
    switch (stateChange.current) {
      case "connected": {
        this.isOnline = true;

        // Calculate offline duration and prepare context
        const offlineDurationMs = this.offlineSince ? Date.now() - this.offlineSince : 0;
        const reconnectContext: ReconnectContext = {
          previousState,
          lastOfflineState: this.lastOfflineState,
          offlineDurationMs,
          connectionId: this.realtime?.connection.id,
        };

        // Clear offline tracking
        this.offlineSince = null;
        this.lastOfflineState = null;

        // Only trigger reconnection callback if this is NOT the first connection
        if (this.hasConnectedBefore) {
          // Pass context to coordinator - it will decide whether to resync
          // Note: DO NOT call syncChannels() here - coordinator handles resync pipeline
          this.lifecycleCallbacks.onReconnected(this.realtime?.connection.id, reconnectContext);
        } else {
          this.hasConnectedBefore = true;
          // First connection - sync state immediately
          this.syncConnectionState();
        }
        break;
      }

      case "connecting":
        // "connecting" is transient - do NOT treat as offline
        // Keep existing isOnline state, just log
        // Only call onReconnecting for UI indicator, but don't change isOnline
        if (this.hasConnectedBefore && previousState !== "connected") {
          this.lifecycleCallbacks.onReconnecting();
        }
        break;

      case "disconnected":
      case "suspended":
        // Track when we first went offline
        if (this.offlineSince === null) {
          this.offlineSince = Date.now();
        }
        // Track the offline state (suspended takes precedence for resync decisions)
        if (stateChange.current === "suspended" || this.lastOfflineState !== "suspended") {
          this.lastOfflineState = stateChange.current as OfflineState;
        }
        this.isOnline = false;
        this.lifecycleCallbacks.onReconnecting();
        break;

      case "failed":
      case "closed":
        this.offlineSince = null;
        this.lastOfflineState = null;
        this.isOnline = false;
        this.lifecycleCallbacks.onDisconnected();
        break;

      default:
        break;
    }
  }

  /**
   * Handle incoming realtime message and route to appropriate handler
   * Message structure:
   * - message.name: "Student", "Whiteboard", "Teacher" (event name)
   * - message.data: { payload: { Action: string, Data: any }, sender: string }
   */
  private async handleRealtimeMessage(message: AblyMessage): Promise<void> {
    if (!this.realtimeHandlerMap) {
      return;
    }

    const eventName = message.name;
    if (!eventName) {
      return;
    }
    const rawData = message.data;

    let realtimeMessage: RealtimeMessage;
    try {
      realtimeMessage = typeof rawData === "string" ? JSON.parse(rawData) : rawData;
    } catch (error) {
      return;
    }

    if (!realtimeMessage.payload || !realtimeMessage.payload.action) {
      return;
    }

    const action = realtimeMessage.payload.action;
    const data = realtimeMessage.payload.data;
    const sender = realtimeMessage.sender ?? message.clientId ?? "";

    const handlerKey = createRealtimeHandlerKey(eventName, action);
    const handler = this.realtimeHandlerMap.get(handlerKey);

    if (handler) {
      try {
        await handler(data, sender);
      } catch (error) {
        //
      }
    } else {
      //
    }
  }

  private async ensureConnected() {
    if (!this.isOnline) {
      await this.connect();
    }
  }

  /**
   * Internal sync channels implementation (called within mutex)
   */
  private async syncChannelsInternal() {
    if (!this.realtime) {
      throw new Error("Realtime transport is not initialized. Call init() before using the transport.");
    }

    if (!this.manualChannelOverride) {
      this.desiredChannelNames = this.normalizeChannelNames(this.options.realtime.channelName);
    }

    const desiredSet = new Set(this.desiredChannelNames);

    // Detach channels that are no longer needed
    for (const [name, channel] of Array.from(this.channelMap.entries())) {
      if (!desiredSet.has(name)) {
        this.unsubscribeAllFromChannel(channel);
        this.channelMap.delete(name);

        try {
          await channel.detach();
        } catch (error) {
          //
        }
      }
    }

    // Attach new channels and subscribe listener immediately after each attach
    // This minimizes the window where messages could be missed
    for (const name of this.desiredChannelNames) {
      if (!this.channelMap.has(name)) {
        const channel = this.realtime.channels.get(name);
        this.channelMap.set(name, channel);
        // Explicitly attach the channel to ensure it's ready to receive messages
        try {
          await channel.attach();
          // Subscribe listener IMMEDIATELY after attach to minimize message loss window
          if (this.realtimeListener) {
            channel.subscribe(this.realtimeListener);
          }
        } catch (error) {
          //
        }
      }
    }
  }

  /**
   * Sync channels (public, with mutex)
   */
  async syncChannels() {
    return this.withChannelOpMutex(() => this.syncChannelsInternal());
  }

  /**
   * Ensure channels are ready after reconnection WITHOUT detaching/reattaching
   * This is for reconnect recovery - only subscribes listeners and ensures channels attached
   * Use this instead of syncChannels() when you just want to ensure readiness after reconnect
   * Serialized behind channelOpMutex
   */
  async ensureReadyAfterReconnect(): Promise<void> {
    return this.withChannelOpMutex(async () => {
      if (!this.realtime) {
        return;
      }
      // Subscribe listener to all channels (with unsubscribe first to avoid duplicates)
      if (this.realtimeListener) {
        this.subscribeRealtimeHandlerToAllChannels();
      }
      // Ensure all channels are attached
      await this.ensureAllChannelsAttachedInternal();
    });
  }

  private subscribeRealtimeHandlerToAllChannels() {
    const listener = this.realtimeListener;
    if (!listener) {
      return;
    }
    // Unsubscribe first to avoid duplicate subscriptions when called multiple times
    this.channelMap.forEach((channel) => channel.unsubscribe(listener));
    // Then subscribe to all channels
    this.channelMap.forEach((channel) => this.subscribeRealtimeHandlerToChannel(channel, listener));
  }

  /**
   * Internal ensure all channels attached (called within mutex)
   */
  private async ensureAllChannelsAttachedInternal(): Promise<void> {
    const attachPromises = Array.from(this.channelMap.entries()).map(async ([_name, channel]) => {
      if (channel.state !== "attached") {
        try {
          await channel.attach();
        } catch (_error) {
          //
        }
      } else {
        //
      }
    });
    await Promise.all(attachPromises);
  }

  /**
   * Ensure all channels are attached (public, with mutex)
   */
  async ensureAllChannelsAttached(): Promise<void> {
    return this.withChannelOpMutex(() => this.ensureAllChannelsAttachedInternal());
  }

  private subscribeRealtimeHandlerToChannel(channel: AblyRealtimeChannel, listener: AblyMessageCallback<AblyMessage>) {
    channel.subscribe(listener);
  }

  private unsubscribeRealtimeHandlersFromAllChannels(clearListener = false) {
    const listener = this.realtimeListener;
    if (listener) {
      this.channelMap.forEach((channel) => channel.unsubscribe(listener));
    }
    if (clearListener) {
      this.realtimeListener = undefined;
      this.realtimeHandlerMap = undefined;
    }
  }

  private unsubscribeAllFromChannel(channel: AblyRealtimeChannel) {
    const listener = this.realtimeListener;
    if (listener) {
      channel.unsubscribe(listener);
    }
  }

  private normalizeChannelNames(input: string | string[] | (() => string | string[]) | undefined): string[] {
    const resolved = typeof input === "function" ? input() : input;
    const names = Array.isArray(resolved) ? resolved : [resolved];
    const normalized = names.filter((name): name is string => typeof name === "string" && !!name);
    if (!normalized.length) {
      throw new Error("Realtime channel name is not defined.");
    }
    return Array.from(new Set(normalized));
  }

  private async sendToSpecificChannel<T = any>(config: SendToChannelConfig<T>, channelName?: string): Promise<boolean> {
    const { event, action, data, senderId } = config;

    if (!this.realtime) {
      return false;
    }

    if (!channelName) {
      return false;
    }

    try {
      const channel = this.realtime.channels.get(channelName);

      const message: RealtimeMessage<T> = {
        payload: {
          action,
          data,
        },
        sender: senderId,
      };

      await channel.publish(event, message);
      return true;
    } catch (error) {
      return false;
    }
  }
}
